import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Tooltip,
  Box, Typography, ToggleButtonGroup, ToggleButton, Slider, Autocomplete, TextField, Alert, CircularProgress,
} from '@mui/material';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { createModuleControlApi } from '../../services/api/moduleControlApi';
import { tokens } from '../../theme/theme';

const STATUS_OPTIONS = ['active', 'paused', 'disabled'];
const SCHEDULE_SUGGESTIONS = ['hourly', 'every_6_hours', 'daily', 'weekly', 'manual'];
const STATUS_COLOR = { active: tokens.severity.low, paused: tokens.severity.medium, disabled: tokens.severity.high, running: tokens.severity.low };

/**
 * Drop into any module page's PageHeader `action` slot:
 *   <ModuleControlPanel basePath="/threat-intel" moduleLabel="Threat Intelligence" />
 * basePath must match that service's FastAPI root_path (see its app/main.py).
 * Pass method="patch" for automation-engine-service, which uses PATCH for
 * status/config where every other service uses PUT.
 */
export default function ModuleControlPanel({ basePath, moduleLabel, method = 'put' }) {
  const api = createModuleControlApi(basePath, { method });
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null);
  const [draftSensitivity, setDraftSensitivity] = useState(50);
  const [draftSchedule, setDraftSchedule] = useState('hourly');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.fetchState();
      setState(data);
      setDraftSensitivity(data.sensitivity);
      setDraftSchedule(data.schedule);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load module state.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const handleStatusChange = async (_, next) => {
    if (!next || next === state?.status) return;
    setSaving(true);
    setError(null);
    try {
      const data = await api.updateStatus(next);
      setState(data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not update status.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    setError(null);
    try {
      const data = await api.updateConfig({ sensitivity: draftSensitivity, schedule: draftSchedule });
      setState(data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not save configuration.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Tooltip title="Engine integration settings">
        <IconButton onClick={() => setOpen(true)} sx={{ color: tokens.text.secondary, border: `1px solid ${tokens.border}` }}>
          <SettingsRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{moduleLabel || 'Module'} — Engine Integration</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={26} /></Box>
          ) : state && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
              <Box>
                <Typography variant="caption" sx={{ color: tokens.text.muted, textTransform: 'uppercase' }}>Status</Typography>
                <ToggleButtonGroup exclusive fullWidth size="small" value={state.status} onChange={handleStatusChange} disabled={saving} sx={{ mt: 0.5 }}>
                  {STATUS_OPTIONS.map((s) => (
                    <ToggleButton key={s} value={s} sx={{ color: state.status === s ? STATUS_COLOR[s] : tokens.text.secondary }}>{s}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: tokens.text.muted, textTransform: 'uppercase' }}>Sensitivity ({draftSensitivity})</Typography>
                <Slider value={draftSensitivity} onChange={(_, v) => setDraftSensitivity(v)} min={0} max={100} disabled={saving} />
              </Box>
              <Autocomplete
                freeSolo
                options={SCHEDULE_SUGGESTIONS}
                value={draftSchedule}
                onInputChange={(_, v) => setDraftSchedule(v)}
                renderInput={(params) => <TextField {...params} label="Schedule" size="small" />}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>Close</Button>
          <Button variant="contained" onClick={handleSaveConfig} disabled={loading || saving || !state}>
            {saving ? 'Saving…' : 'Save Configuration'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
